/* Exit-intent popup.
   Disabled on the assessment/questionnaire page. Once per session via sessionStorage['aas-exit'].
   Desktop: mouseout top edge after ≥8s. Mobile: rapid upward scroll after >900px scroll depth. */

import { EXIT_STORAGE_KEY } from './aas-assessment'

export type ExitIntentOptions = {
  /** When true, never show (assessment page). */
  disabled?: boolean
  primaryHref?: string
}

export function initExitIntent(root: ParentNode = document, options: ExitIntentOptions = {}) {
  if (options.disabled) {
    return { open: () => {}, close: () => {}, get shown() { return false } }
  }

  const primaryHref = options.primaryHref ?? '/questionnaire/'
  let exitShown = false
  const exitT0 = Date.now()

  let exitEl = root.querySelector('[data-aas-exit]') as HTMLElement | null
  if (!exitEl) {
    exitEl = document.createElement('div')
    exitEl.className = 'aas-exit'
    exitEl.setAttribute('data-aas-exit', '')
    exitEl.setAttribute('role', 'dialog')
    exitEl.setAttribute('aria-modal', 'true')
    exitEl.setAttribute('aria-labelledby', 'aas-exit-title')
    exitEl.hidden = true
    exitEl.innerHTML = `
      <div class="aas-exit-card">
        <button type="button" class="aas-exit-x" data-aas-exit-close aria-label="Close">&times;</button>
        <div class="aas-exit-head">
          <svg class="aas-exit-logo kite" viewBox="0 0 360 344" aria-hidden="true"><use href="#kite"/></svg>
          <span class="aas-exit-pill"><i></i> Before you go</span>
        </div>
        <h2 id="aas-exit-title" class="aas-exit-title">Your team used AI today. <span class="aas-exit-pop">Could you name one thing it did?</span></h2>
        <p>Most leaders can&rsquo;t. The AI Company Assessment takes two minutes and shows you exactly where you&rsquo;re exposed: what you can see, what it costs, what&rsquo;s installed, and whether you&rsquo;re ready for the next two years.</p>
        <div class="aas-exit-row">
          <a class="pill pill-ink" data-aas-exit-cta href="${primaryHref}">Take the AI Company Assessment</a>
          <button type="button" class="aas-exit-later" data-aas-exit-close>Not now, I&rsquo;ll keep guessing</button>
        </div>
        <div class="aas-exit-meta">
          <span>8 questions</span><span>2 minutes</span><span>Instant readiness report</span><span>No newsletter</span>
        </div>
      </div>
    `
    const host = root instanceof Document ? root.body : (root as HTMLElement)
    host.appendChild(exitEl)
  }

  const canExit = () => {
    if (exitShown || !exitEl || !exitEl.hidden) return false
    try {
      if (sessionStorage.getItem(EXIT_STORAGE_KEY)) return false
    } catch { /* ignore */ }
    return Date.now() - exitT0 > 8000
  }

  const open = () => {
    if (!canExit() || !exitEl) return
    exitShown = true
    try { sessionStorage.setItem(EXIT_STORAGE_KEY, '1') } catch { /* ignore */ }
    exitEl.hidden = false
    document.body.style.overflow = 'hidden'
    const cta = exitEl.querySelector('[data-aas-exit-cta]') as HTMLElement | null
    cta?.focus()
  }

  const close = () => {
    if (!exitEl) return
    exitEl.hidden = true
    document.body.style.overflow = ''
  }

  document.addEventListener('mouseout', (e) => {
    if (!e.relatedTarget && e.clientY <= 0) open()
  })

  const hist: { t: number; y: number }[] = []
  let maxY = 0
  window.addEventListener('scroll', () => {
    const t = Date.now()
    const y = scrollY
    hist.push({ t, y })
    while (hist.length && t - hist[0]!.t > 450) hist.shift()
    maxY = Math.max(maxY, y)
    if (
      matchMedia('(hover: none)').matches
      && maxY > 900
      && hist[0]
      && hist[0].y - y > 240
      && y < maxY * 0.7
    ) {
      open()
    }
  }, { passive: true })

  exitEl.addEventListener('click', (e) => {
    const t = e.target as HTMLElement
    if (t === exitEl || t.closest('[data-aas-exit-close]')) close()
  })
  exitEl.querySelector('[data-aas-exit-cta]')?.addEventListener('click', () => close())
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && exitEl && !exitEl.hidden) close()
  })

  return { open, close, get shown() { return exitShown } }
}
